import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import {
  AdLeaderboardLevel,
  AdLeaderboardQuery,
  AdLeaderboardReportDto,
  AdLeaderboardRowDto,
  AdLeaderboardSort,
  MarketingAcquisitionService
} from '../../../../services/marketing-acquisition.service';
import { PaginationComponent } from '../../../shared/pagination/pagination.component';
import { SelectInputComponent, SelectOption } from '../../../shared/select-input/select-input.component';
import type { AcquisitionDateRange } from '../acquisition.component';

const CTA_LABELS: Record<AdLeaderboardRowDto['cta'], { label: string; classes: string }> = {
  whatsapp: { label: 'WhatsApp', classes: 'bg-green-100 text-green-800' },
  web: { label: 'Website', classes: 'bg-blue-100 text-blue-800' },
  mixed: { label: 'Mixed', classes: 'bg-purple-100 text-purple-800' },
  unknown: { label: 'Unknown', classes: 'bg-gray-100 text-gray-600' }
};

/**
 * V3 — Meta ads ranked by what they produced (leads → pickups → revenue) and what it cost, across
 * both CTAs, at ad, ad set or campaign level.
 */
@Component({
  selector: 'app-ad-leaderboard',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, SelectInputComponent],
  templateUrl: './ad-leaderboard.component.html'
})
export class AdLeaderboardComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  readonly levelOptions: SelectOption[] = [
    { value: 'ad', label: 'Ads' },
    { value: 'adset', label: 'Ad sets' },
    { value: 'campaign', label: 'Campaigns' }
  ];
  readonly ctaOptions: SelectOption[] = [
    { value: '', label: 'All CTAs' },
    { value: 'whatsapp', label: 'WhatsApp CTA' },
    { value: 'web', label: 'Website CTA' }
  ];
  readonly sortOptions: SelectOption[] = [
    { value: 'spend', label: 'Highest spend' },
    { value: 'leads', label: 'Most leads' },
    { value: 'pickups', label: 'Most pickups' },
    { value: 'revenueRoas', label: 'Best revenue ROAS' },
    { value: 'cac', label: 'Lowest CAC' }
  ];

  level: AdLeaderboardLevel = 'ad';
  cta = '';
  sort: AdLeaderboardSort = 'spend';
  search = '';
  currentPage = 1;
  pageSize = 25;

  loading = false;
  errorMessage = '';
  report: AdLeaderboardReportDto | null = null;

  private readonly load$ = new Subject<AdLeaderboardQuery>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  get totalPages(): number {
    return this.report ? Math.max(1, Math.ceil(this.report.totalCount / this.pageSize)) : 1;
  }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(query => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getAdLeaderboard(query).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => {
        this.report = report;
        this.loading = false;
      });

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.reload(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Re-queries; any change other than paging starts again from page 1. */
  reload(resetPage = false): void {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.load$.next({
      from: this.range.from,
      to: this.range.to,
      level: this.level,
      cta: this.cta,
      search: this.search,
      sort: this.sort,
      pageIndex: this.currentPage - 1,
      pageSize: this.pageSize
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.reload();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.reload(true);
  }

  ctaBadge(cta: AdLeaderboardRowDto['cta']): { label: string; classes: string } {
    return CTA_LABELS[cta] ?? CTA_LABELS.unknown;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load the ad leaderboard. Please try again.';
    }
  }
}
